import React, { useState, useCallback } from "react";
import { ReactComponent as CancelIcon } from "assets/img/icons/cancel.svg";
import { ReactComponent as TickIcon } from "assets/img/icons/tick.svg";
import { ReactComponent as Edit } from "assets/img/icons/Edit.svg";
import UserBasicInfo from "../UserBasicInfo/UserBasicInfo";
import UserAvailabilityHours from "../UserAvailabilityHours/UserAvailabilityHours";
import { useFormik } from "formik";
import UserService from "services/UserService";
import { useToasts } from "react-toast-notifications";
import * as Yup from "yup";
import FunPics from "../FunPics/FunPics";
import UserCompanyInfo from "../UserCompanyInfo/UserCompanyInfo";
import UserStrength from "../UserStrength/UserStrength";
import UserVerifyEmail from "../UserVerifyEmail/UserVerifyEmail";

var styles = {
  editBasicInfo: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "10px",
    marginTop: "10px",
  },
  iconsWidth: {
    width: "24px",
    height: "24px",
  },
};

export default ({ profile, user, setUser, setProfile, isOwner }) => {
  const classes = styles;
  const { addToast } = useToasts();
  const [domainError, setDomainError] = useState("");
  const [updatedEmail, setUpdatedEmail] = useState("");
  const [editable, setEditable] = useState(false);

  const [workingStart, workingEnd, personalStart, personalEnd] = [
    profile?.profile?.working_hours_start ?? "8:00",
    profile?.profile?.working_hours_end ?? "17:00",
    profile?.profile?.personal_hours_start ?? "8:00",
    profile?.profile?.personal_hours_end ?? "17:00",
  ];

  const workingHoursStart = useCallback(() => {
    return getDateWithTime.apply(this, [...workingStart.split(":")]);
  }, [workingStart]);

  const workingHoursEnd = useCallback(() => {
    return getDateWithTime.apply(this, [...workingEnd.split(":")]);
  }, [workingEnd]);

  const personalHoursStart = useCallback(() => {
    return getDateWithTime.apply(this, [...personalStart.split(":")]);
  }, [personalStart]);

  const personalHoursEnd = useCallback(() => {
    return getDateWithTime.apply(this, [...personalEnd.split(":")]);
  }, [personalEnd]);

  const formik = useFormik({
    initialValues: {
      first_name: profile.first_name ?? "",
      last_name: profile.last_name ?? "",
      email: profile.email ?? "",
      phone: profile.profile.phone ?? "",
      location: profile.profile.location ?? "",
      geo_location: profile.profile.geo_location ?? {},
      timezone: profile.profile.timezone ?? "",
      about_skills: profile.profile.about_skills ?? "",
      working_hours_start: workingHoursStart(),
      working_hours_end: workingHoursEnd(),
      personal_hours_start: personalHoursStart(),
      personal_hours_end: personalHoursEnd(),
      company_joining_date: profile.profile.company_joined
        ? new Date(profile.profile.company_joined)
        : "",
      company_name: profile.profile.company_name ?? "",
      per_of_time_remote: profile.profile.remote_work_percentage ?? "",
      per_of_time_office: profile.profile.office_work_percentage ?? "",
      your_strengths: "",
    },
    validationSchema: Yup.object().shape(
      {
        first_name: Yup.mixed().required(),
        last_name: Yup.mixed().required(),
        email: Yup.string()
          .email("Must be a valid Email.")
          .required("Email is required."),
      },
      ["first_name", "last_name", "email"]
    ),
    onSubmit: async (values) => {
      setEditable(false);
      if (!isOwner) {
        return;
      }
      try {
        const body = {
          ...values,
          working_hours_start: getStringifiedTime(values.working_hours_start),
          working_hours_end: getStringifiedTime(values.working_hours_end),
          personal_hours_start: getStringifiedTime(values.personal_hours_start),
          personal_hours_end: getStringifiedTime(values.personal_hours_end),
        };
        await UserService.updateProfileBio(profile, body);

        if (values.about_skills !== profile?.profile?.about_skills) {
          await UserService.updateAboutSkills(values.about_skills);
        }

        if (profile.email !== values.email) {
          setUpdatedEmail(values.email);
        }

        addToast("User profile updated successfully. ", {
          appearance: "success",
        });
      } catch (error) {
        console.log(error);
        if (error.response.data.status === 412) {
          setDomainError(
            "Your company domain is currently not registered with ReTeam."
          );
        }
      }

      setProfile({
        ...profile,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        profile: {
          ...profile.profile,
          working_hours_start: getStringifiedTime(values.working_hours_start),
          working_hours_end: getStringifiedTime(values.working_hours_end),
          personal_hours_start: getStringifiedTime(values.personal_hours_start),
          personal_hours_end: getStringifiedTime(values.personal_hours_end),
          phone: values.phone,
          location: values.location,
          timezone: values.timezone,
          about_skills: values.about_skills,
          company_name: values.company_name,
          company_joined: values.company_joining_date,
        },
      });
      if (
        user.first_name !== values.first_name ||
        user.last_name !== values.last_name ||
        user.email !== values.email
      ) {
        setUser({
          ...user,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
        });
      }
    },
  });

  const setNewEmail = ({ email }) => {
    setUser({ ...user, email: email });
    setProfile({ ...profile, email: email });
  };

  return (
    <div>
      <UserVerifyEmail
        openModal={updatedEmail ? true : false}
        previousEmail={profile?.email}
        email={updatedEmail}
        setNewEmail={setNewEmail}
        closeModal={() => setUpdatedEmail("")}
      />
      {isOwner && (
        <div style={classes.editBasicInfo}>
          {!editable && (
            <Edit
              style={classes.iconsWidth}
              onClick={() => {
                setEditable(!editable);
              }}
            />
          )}
          {editable && (
            <>
              <TickIcon
                style={classes.iconsWidth}
                onClick={formik.handleSubmit}
              />
              <CancelIcon
                style={classes.iconsWidth}
                onClick={() => {
                  formik.resetForm();
                  setEditable(false);
                }}
              />
            </>
          )}
        </div>
      )}
      <UserBasicInfo
        editable={editable}
        formik={formik}
        profile={profile}
        user={user}
        setUser={setUser}
        setProfile={setProfile}
        isOwner={isOwner}
        domainError={domainError}
      />
      <UserCompanyInfo editable={editable} formik={formik} />
      <UserStrength editable={editable} formik={formik} />
      <UserAvailabilityHours
        editable={editable}
        formik={formik}
        profile={profile}
        setProfile={setProfile}
        isOwner={isOwner}
      />
      <FunPics profile={profile} setProfile={setProfile} isOwner={isOwner} />
    </div>
  );
};

const getDateWithTime = (hours, minutes) => {
  let date = new Date();

  date.setHours(parseInt(hours));
  date.setMinutes(parseInt(minutes));

  return date;
};

const getStringifiedTime = (time) => {
  const date = new Date(time);

  return `${date.getHours()}:${date.getMinutes()}`;
};
